// How to add another Instagram account: one Chrome profile per account, the extension in each,
// and one explicit "Connect @handle" click in the extension for a brand-new account.

import { Callout, Card, buttonClass } from "@gf/ui";
import { ArrowUpRight, Download, Info } from "lucide-react";
import { Link } from "react-router";
import { planLabel } from "../../lib/format";
import { biggerPlans } from "../../lib/workspace";
import { ExtensionGlyph } from "../IgGlyph";
import "./ig-workspaces.css";

export const ADD_ACCOUNT_ANCHOR = "add-account";

export function AddIgAccountGuide({ used, limit }: { used: number; limit: number }) {
  const atLimit = used >= limit;
  const more = biggerPlans(limit);
  return (
    <Card
      id={ADD_ACCOUNT_ANCHOR}
      className="gfp-ws-guide"
      title={atLimit ? "You're at your plan's limit" : "Add another Instagram account"}
      subtitle={
        atLimit
          ? `Your plan runs ${limit} Instagram ${limit === 1 ? "account" : "accounts"}, and all of them are taken.`
          : "Each Instagram account runs in its own Chrome profile. Five steps, about three minutes."
      }
    >
      {atLimit ? (
        <Callout tone="neutral" title="Need room for another account?">
          Disconnect one you don't need below
          {more.length
            ? `, or move up to ${more.map((p) => `${planLabel(p.plan)} (${p.limit} accounts)`).join(" or ")}. Ask about a bigger plan and it's yours.`
            : "."}
        </Callout>
      ) : null}
      <ol className="gfp-ws-guide__steps">
        <li>
          <b>Make a Chrome profile for the account.</b> Click your profile picture at the top right of Chrome, then{" "}
          <i>Add</i>. One profile per Instagram account keeps their logins apart.
        </li>
        <li>
          <b>Install the GramFather extension in that profile.</b>{" "}
          <Link to="/extension" className="gfp-ws-guide__link">
            <Download size={13} aria-hidden="true" /> Get the extension
          </Link>
        </li>
        <li>
          <b>Sign in to GramFather in that profile.</b> Open app.gramfather.com there and log in; the extension signs in
          with you automatically.{" "}
          <Link to="/connect-extension" className="gfp-ws-guide__link">
            <ExtensionGlyph size={13} /> Extension setup
          </Link>
        </li>
        <li>
          <b>Log into the Instagram account</b> on{" "}
          <a href="https://www.instagram.com/" target="_blank" rel="noreferrer" className="gfp-ws-guide__link">
            instagram.com <ArrowUpRight size={13} aria-hidden="true" />
          </a>{" "}
          in that profile.
        </li>
        <li>
          <b>Open the extension and click "Connect @handle".</b> It shows up here within a minute
          {atLimit ? " once you have a free slot" : ""}.
        </li>
      </ol>
      <p className="gfp-ws-guide__note">
        <Info size={14} aria-hidden="true" />
        <span>
          Accounts you've already connected are picked up automatically in any profile where they're logged in, no click
          needed. Only one browser runs an account at a time; any other browser logged into it stands by.
        </span>
      </p>
      {atLimit ? null : (
        <div className="gfp-ws-guide__actions">
          <Link to="/extension" className={buttonClass({ variant: "secondary", size: "sm" })}>
            <Download size={15} aria-hidden="true" /> Get the extension
          </Link>
        </div>
      )}
    </Card>
  );
}
